import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

const searchSchema = z.object({
  error: z.string().optional(),
});

export const Route = createFileRoute("/admin/login")({
  ssr: false,
  validateSearch: (raw) => searchSchema.parse(raw),
  head: () => ({
    meta: [
      { title: "Admin sign in — Poonam Kapur Healthy Kitchen" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLoginPage,
});

function AdminLoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(
    search.error === "not_admin"
      ? "That account doesn't have admin access."
      : null,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      if (data.user) {
        navigate({ to: "/admin", replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (signInError) {
        setError("Invalid email or password.");
        setSubmitting(false);
        return;
      }
      navigate({ to: "/admin", replace: true });
    } catch {
      setError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[#fdfcf6] px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-10 text-center">
          <h1 className="text-4xl font-display font-bold tracking-tight text-[#2d4a3e]">
            Admin Portal
          </h1>
          <p className="mt-4 text-[10px] font-sans font-bold text-[#2d4a3e]/60 uppercase tracking-[0.2em]">
            Internal Access Only
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-2xl border border-[#2d4a3e]/10 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          noValidate
        >
          {error ? (
            <Alert variant="destructive" role="alert" aria-live="assertive" className="border-destructive/20 bg-destructive/5 text-destructive">
              <AlertDescription className="font-medium">{error}</AlertDescription>
            </Alert>
          ) : null}
          
          <div className="space-y-2">
            <Label 
              htmlFor="admin-email"
              className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#2d4a3e]/70"
            >
              Email Address
            </Label>
            <Input
              id="admin-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              className="h-12 border-[#2d4a3e]/10 bg-[#fdfcf6]/50 focus-visible:ring-[#2d4a3e] rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label 
              htmlFor="admin-password"
              className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#2d4a3e]/70"
            >
              Password
            </Label>
            <div className="relative">
              <Input
                id="admin-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                className="h-12 border-[#2d4a3e]/10 bg-[#fdfcf6]/50 pr-10 focus-visible:ring-[#2d4a3e] rounded-xl"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2d4a3e]/40 hover:text-[#2d4a3e] focus:outline-none transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88L4.62 4.62"/><path d="M8.47 16.39L2 22"/><path d="M2 2l20 20"/><path d="M9.51 9.51L8.59 8.59"/><path d="M12.73 17.27l-.73.73"/><path d="M16.41 16.41L17.27 17.27"/><path d="M21.16 7.84C21.69 8.57 22 9.48 22 12c0 2.5-2.5 4.5-5 4.5-1.05 0-2.02-.35-2.82-.94"/><path d="M6.59 6.59C6.07 7.21 5.48 7.9 5 8.5c-1.5 2-2 3.5-2 3.5 0 0 5 8 11 8 1.41 0 2.7-.44 3.82-1.22"/><path d="M10.59 10.59c-.38.38-.59.89-.59 1.41 0 1.1.9 2 2 2 .52 0 1.03-.21 1.41-.59"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <Button 
            type="submit" 
            className="h-12 w-full font-bold uppercase tracking-[0.2em] bg-[#2d4a3e] text-[#fdfcf6] hover:bg-[#2d4a3e]/90 transition-all rounded-xl shadow-lg shadow-[#2d4a3e]/10 active:scale-[0.98]" 
            disabled={submitting}
          >
            {submitting ? "Authenticating…" : "Sign In"}
          </Button>

          <div className="pt-2 text-center">
            <button 
              type="button"
              className="text-[10px] font-bold text-[#2d4a3e]/40 hover:text-[#2d4a3e] uppercase tracking-widest transition-colors"
              onClick={() => window.location.href = '/'}
            >
              ← Back to Website
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}